import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useEscapeKey, useReduxState } from 'hooks';
import Mousetrap from 'mousetrap';
import { breakpoints } from 'styles';
import { Button } from 'components/common';
import { Icon } from 'components/common/icons';

import type { ButtonCategory, ButtonSize } from 'components/common/Button';
import type { IconProp } from 'components/common/icons/Icon';

interface Props extends React.PropsWithChildren {
  after?: string | React.ReactNode;
  align?: 'left' | 'right';
  alignVertical?: 'top' | 'bottom';
  arrowStyle?: React.CSSProperties;
  before?: string | React.ReactNode;
  buttonClassName?: string;
  buttonCustomBackgroundColor?: string;
  buttonCustomBackgroundColorHover?: string;
  buttonStyle?: React.CSSProperties;
  category?: ButtonCategory;
  contentStyle?: React.CSSProperties;
  disabled?: boolean;
  dropdownStyle?: React.CSSProperties;
  icon?: IconProp;
  iconStyle?: React.CSSProperties;
  isMenu?: boolean;
  label?: string;
  onButtonMouseEnter?: () => void;
  onButtonMouseLeave?: () => void;
  onContentScroll?: () => void;
  preventDefault?: boolean;
  renderAsFixed?: boolean;
  renderChildrenOnlyWhenVisible?: boolean;
  renderInPortal?: boolean;
  showArrow?: boolean;
  size?: ButtonSize;
  style?: React.CSSProperties;
  styleContentButtons?: boolean;
  title?: string | React.ReactNode;
  titleStyle?: React.CSSProperties;
  tooltip?: string;
  underlineOnHover?: boolean;
}

const Dropdown = ({
  after,
  align,
  alignVertical = 'top',
  arrowStyle,
  before,
  buttonClassName = '',
  buttonCustomBackgroundColor,
  buttonCustomBackgroundColorHover,
  buttonStyle,
  category = 'transparent',
  children,
  contentStyle,
  disabled,
  dropdownStyle,
  icon,
  iconStyle,
  isMenu,
  label,
  onButtonMouseEnter,
  onButtonMouseLeave,
  onContentScroll,
  preventDefault,
  renderAsFixed,
  renderChildrenOnlyWhenVisible = false,
  renderInPortal = false,
  showArrow,
  size = 'header',
  style,
  styleContentButtons = true,
  title,
  titleStyle,
  tooltip,
  underlineOnHover = false
}: Props) => {
  const { theme } = useReduxState();

  const buttonElement = useRef<HTMLButtonElement>(undefined);
  const dropdownElement = useRef<HTMLDivElement>(undefined);
  const dropdownContentElement = useRef<HTMLDivElement>(undefined);

  const focusIndex = useRef<number>(-1);

  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [fixedVerticalAlignment, setFixedVerticalAlignment] = useState<
    'bottom' | 'top' | null
  >(null);
  const [isMounted, setIsMounted] = useState(false);
  const [renderChildren, setRenderChildren] = useState(
    renderChildrenOnlyWhenVisible !== true
  );

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Set up and torn down from the same render, so each remove gets the exact listener that was added
  useEffect(() => {
    if (!dropdownVisible) return;

    document.addEventListener('click', handleOutsideMouseClick, {
      capture: true
    });
    Mousetrap.bind('up', focusPreviousOption);
    Mousetrap.bind('down', focusNextOption);

    const children = getFocusableContentChildren();
    for (const child of children) {
      child.addEventListener('focus', handleChildFocus, { capture: true });
    }

    return () => {
      document.removeEventListener('click', handleOutsideMouseClick, {
        capture: true
      });
      Mousetrap.unbind('up');
      Mousetrap.unbind('down');

      for (const child of children) {
        child.removeEventListener('focus', handleChildFocus, { capture: true });
      }
    };
  }, [dropdownVisible]);

  useLayoutEffect(() => {
    if (!dropdownVisible) {
      setFixedVerticalAlignment(null);
      return;
    }

    // verify whether the dropdown will overflow the bounds of the viewport
    const margin = 10;

    const { height, top } =
      dropdownContentElement.current?.getBoundingClientRect() || {};

    const viewportBottom = window.visualViewport?.height ?? window.innerHeight;
    const overflowsBottom = top + height + margin > viewportBottom;
    const overflowsTop = top - margin < 0;

    setFixedVerticalAlignment(
      overflowsBottom ? 'bottom' : overflowsTop ? 'top' : null
    );
  }, [dropdownVisible]);

  const focusNextOption = (ev) => {
    ev.preventDefault();
    const children = getFocusableContentChildren();
    if (!children?.length) return;

    if (focusIndex.current < children.length - 1) {
      children[focusIndex.current + 1].focus();
    } else {
      children[0].focus();
    }
  };

  const focusPreviousOption = (ev) => {
    ev.preventDefault();
    const children = getFocusableContentChildren();
    if (!children?.length) return;

    if (focusIndex.current > 0) {
      children[focusIndex.current - 1].focus();
    } else {
      children[children.length - 1].focus();
    }
  };

  const getFocusableContentChildren = () => {
    const children = dropdownContentElement.current?.childNodes || [];
    return Array.from(children).filter((node) =>
      ['a', 'button'].includes(node.tagName?.toLowerCase())
    );
  };

  const handleButtonKeyDown = (ev) => {
    if (ev.target !== ev.currentTarget) return;

    if (ev.keyCode === 40 && !dropdownVisible) {
      // arrow down
      toggleDropdown(ev);
    } else if (ev.keyCode === 38 && dropdownVisible) {
      // arrow up
      handleClose();
    }
  };

  const handleChildFocus = (ev) => {
    const children = getFocusableContentChildren();
    for (let i = 0; i < children.length; i++) {
      if (ev.currentTarget === children[i]) focusIndex.current = i;
    }
  };

  const handleOutsideMouseClick = (ev) => {
    // check if event came from dropdown toggle button
    if (buttonElement.current && buttonElement.current.contains(ev.target)) {
      return;
    }

    // check if event came from button in dropdown that we want to keep open
    let el = ev.target;
    while (
      el &&
      !el.getAttribute('data-keep-open') &&
      !el.classList.contains('data-keep-open')
    ) {
      el = el.parentElement;
    }

    if (
      el &&
      (el.getAttribute('data-keep-open') ||
        el.classList.contains('data-keep-open'))
    ) {
      return;
    }

    hideDropdown();
  };

  const handleClose = () => {
    hideDropdown();
    if (buttonElement.current) buttonElement.current.focus();
  };

  useEscapeKey(dropdownVisible, handleClose);

  const handleDropdownClick = (ev) => {
    if (preventDefault === undefined || preventDefault) ev.preventDefault();
  };

  const hideDropdown = () => {
    if (isMounted) {
      setDropdownVisible(false);
      if (renderChildrenOnlyWhenVisible === true) {
        setTimeout(() => setRenderChildren(false), 200);
      }
    }
  };

  const showDropdown = () => {
    if (renderAsFixed || renderInPortal) {
      const { height, left, right, top } =
        buttonElement.current?.getBoundingClientRect() || {};

      dropdownElement.current.style.position = 'fixed';
      dropdownElement.current.style.minWidth = 'auto';
      dropdownElement.current.style.top = `${top + height + 5}px`;
      if (align === 'left') {
        dropdownElement.current.style.left = `${left}px`;
      } else {
        dropdownElement.current.style.right = `${
          (window.innerWidth ||
            document.documentElement.clientWidth ||
            document.body.clientWidth) - right
        }px`;
      }
    }
    if (renderChildrenOnlyWhenVisible === true) {
      setDropdownVisible(true);
      setRenderChildren(true);
    } else setDropdownVisible(true);
  };

  const toggleDropdown = (ev) => {
    ev.preventDefault();
    ev.stopPropagation();

    if (dropdownVisible) hideDropdown();
    else showDropdown();
  };

  const renderDropdownContent = () => (
    <div
      className={`dropdown ${dropdownVisible && 'visible'}`}
      ref={dropdownElement}
      style={{
        ...(renderAsFixed || renderInPortal
          ? { position: 'fixed' }
          : {
              ...(align === 'left' ? { left: 0 } : { right: 0 }),
              [fixedVerticalAlignment ?? alignVertical]: {
                small: 33,
                header: 35,
                medium: 39,
                large: 43,
                huge: 49
              }[size]
            }),
        ...(dropdownStyle || {})
      }}
      onClick={handleDropdownClick}
    >
      <div
        className={
          `dropdown-content` +
          `${styleContentButtons ? ' style-content-buttons' : ''}`
        }
        ref={dropdownContentElement}
        style={contentStyle}
        {...(isMenu ? { role: 'menu' } : {})}
        onScroll={onContentScroll}
      >
        {renderChildren ? children : null}
      </div>

      <style jsx>{`
        .dropdown {
          opacity: 0;
          height: 0;
          overflow: hidden;
          min-width: calc(100% - 10px);
          position: absolute;
          transform: scale(0.8);
          transform-origin: center top 0;
          transition: all 0.2s cubic-bezier(0.35, 0, 0.25, 1);
          visibility: hidden;
          z-index: 10;
          width: max-content;
        }
        .dropdown.visible {
          height: auto;
          opacity: 1;
          overflow: visible;
          transform: none;
          visibility: visible;
        }
        .dropdown-content {
          background-color: white;
          border-radius: ${theme.dropdownBorderRadius};
          box-shadow: ${theme.dropdownShadow};
          max-height: 370px;
          overflow: auto;
          position: relative;
          padding: 8px 5px;
          z-index: 3;
        }
        .dropdown .style-content-buttons :global(a),
        .dropdown .style-content-buttons :global(button) {
          background-color: white;
          border: 0;
          border-radius: ${theme.buttonBorderRadius};
          color: ${theme.textColorLight};
          cursor: pointer;
          display: block;
          font-family: inherit;
          font-size: 1.4rem;
          font-weight: 600;
          height: auto;
          margin: 0;
          min-width: 140px;
          outline: 0;
          padding: 8px 12px;
          text-align: left;
          text-decoration: none;
          transition: background-color 0.3s, color 0.3s;
          white-space: nowrap;
          width: 100%;
        }
        .dropdown .style-content-buttons :global(a:hover),
        .dropdown .style-content-buttons :global(a:focus),
        .dropdown .style-content-buttons :global(button:hover),
        .dropdown .style-content-buttons :global(button:focus) {
          background-color: ${theme.lightGreyLighter};
        }
        .dropdown .style-content-buttons :global(button:disabled),
        .dropdown .style-content-buttons :global(button:disabled:hover) {
          background-color: white;
          cursor: default;
        }
        .dropdown .style-content-buttons :global(svg) {
          margin-right: 5px;
        }
        @media (max-width: ${breakpoints.small - 1}px) {
          .dropdown {
            max-width: calc(100vw - 60px) !important;
          }
        }
      `}</style>
    </div>
  );

  return (
    <div className="wrapper" style={style}>
      <Button
        active={dropdownVisible}
        aria-expanded={dropdownVisible}
        aria-haspopup={true}
        category={category}
        className={`toggle ${buttonClassName}`}
        customBackgroundColor={buttonCustomBackgroundColor}
        customBackgroundColorHover={buttonCustomBackgroundColorHover}
        disabled={disabled}
        icon={icon}
        iconStyle={iconStyle}
        onClick={toggleDropdown}
        onKeyDown={handleButtonKeyDown}
        onMouseEnter={onButtonMouseEnter}
        onMouseLeave={onButtonMouseLeave}
        ref={buttonElement}
        size={size}
        style={buttonStyle}
        type="button"
        {...(label ? { 'aria-label': label } : {})}
        {...(tooltip ? { 'data-tooltip': tooltip } : {})}
        text={
          before || title || showArrow ? (
            <>
              {before ? <span>{before}</span> : null}

              {title ? (
                <span
                  className="toggle-title"
                  style={{ marginLeft: before ? '5px' : 0, ...titleStyle }}
                >
                  {title}
                </span>
              ) : null}

              {showArrow ? (
                <Icon
                  className="arrow-icon"
                  icon="chevron-down"
                  style={arrowStyle}
                />
              ) : null}

              {after ? <span>{after}</span> : null}
            </>
          ) : null
        }
        underlineOnHover={underlineOnHover}
      />

      {renderInPortal && isMounted
        ? ReactDOM.createPortal(
            renderDropdownContent(),
            document.querySelector('.dropdown-root')
          )
        : renderDropdownContent()}

      <style jsx>{`
        .wrapper {
          display: inline-block;
          position: relative;
        }
        .wrapper :global(.toggle) {
          border-radius: ${theme.buttonBorderRadius};
        }
        .wrapper :global(.arrow-icon) {
          font-size: 1.2rem;
          margin-left: 10px;
        }
      `}</style>
    </div>
  );
};

export default Dropdown;
